/**
 * <s-recently-viewed>: remembers product handles a shopper views (localStorage,
 * this device only) and renders them as a row of cards from /products/<handle>.js.
 * Sets [data-rv-ready] on itself once at least one card renders so the
 * surrounding container can reveal itself with has-[[data-rv-ready]]:block.
 */
const STORE_KEY = 'aaji-recently-viewed';
const MAX_STORED = 12;

const readHandles = () => {
  try {
    const list = JSON.parse(window.localStorage.getItem(STORE_KEY) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
};

const saveHandles = (list) => {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
  } catch {
    /* storage blocked: nothing to remember */
  }
};

class RecentlyViewed extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const current = this.dataset.current || '';
    const limit = Number(this.dataset.limit) || 8;
    let handles = readHandles();

    if (current) {
      handles = [current, ...handles.filter((h) => h !== current)];
      saveHandles(handles);
    }

    const toShow = handles.filter((h) => h !== current).slice(0, limit);
    if (toShow.length) this.#render(toShow);
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  async #render(handles) {
    const root = (this.dataset.root || '/').replace(/\/?$/, '/');
    const { signal } = this.#abort;
    const results = await Promise.all(
      handles.map((handle) =>
        fetch(`${root}products/${encodeURIComponent(handle)}.js`, { signal })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    );

    const products = results.filter((product) => product && product.available !== undefined);
    const stillThere = new Set(products.map((product) => product.handle));
    const stored = readHandles();
    const cleaned = stored.filter((h) => h === this.dataset.current || stillThere.has(h) || !handles.includes(h));
    if (cleaned.length !== stored.length) saveHandles(cleaned);

    const list = this.querySelector('[data-rv-list]');
    if (!list || !products.length) return;
    list.replaceChildren(...products.map((product) => this.#card(product)));
    this.dataset.rvReady = '';
  }

  #money(cents) {
    const value = cents / 100;
    const amount = Number.isInteger(value)
      ? value.toLocaleString('en-IN')
      : value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const format = this.dataset.moneyFormat || 'Rs. {{amount}}';
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  #image(src) {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=480`;
  }

  #card(product) {
    const [titleMain, titleLocal] = String(product.title).split('|').map((part) => part.trim());
    const item = document.createElement('li');
    item.className = 'snap-start';

    const card = document.createElement('a');
    card.href = product.url;
    card.className =
      'group flex h-full flex-col rounded-card bg-surface-secondary p-2 pb-3 text-on-surface no-underline shadow-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent motion-safe:transition-[translate,box-shadow] motion-safe:duration-300 motion-safe:ease-ui md:hover:-translate-y-1 md:hover:shadow-lg';

    const media = document.createElement('span');
    media.className = 'relative block aspect-square overflow-hidden rounded-[calc(var(--radius-card)-0.375rem)] bg-surface';
    const imageSrc = this.#image(product.featured_image);
    if (imageSrc) {
      const img = document.createElement('img');
      img.src = imageSrc;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 480;
      img.height = 480;
      img.className = 'size-full object-cover';
      media.append(img);
    }
    const onSale = product.compare_at_price && product.compare_at_price > product.price;
    if (onSale) {
      const badge = document.createElement('span');
      badge.className =
        'absolute top-2 start-2 rounded-full bg-interactive px-2.5 py-1 text-[11px] font-bold leading-none text-interactive-text';
      badge.textContent = this.dataset.saleLabel || 'Sale';
      media.append(badge);
    }

    const body = document.createElement('span');
    body.className = 'flex grow flex-col gap-1 px-1 pt-2.5';

    const title = document.createElement('span');
    title.className = 'type-body-sm line-clamp-2 font-semibold leading-snug group-hover:text-accent';
    title.textContent = titleMain;
    body.append(title);

    if (titleLocal) {
      const local = document.createElement('span');
      local.className = 'truncate text-xs text-on-surface-secondary';
      local.textContent = titleLocal;
      body.append(local);
    }

    const price = document.createElement('span');
    price.className = 'mt-auto flex flex-wrap items-baseline gap-x-1.5 pt-1';
    const now = document.createElement('bdi');
    now.className = 'text-base font-bold text-on-surface';
    now.textContent = this.#money(product.price);
    price.append(now);
    if (onSale) {
      const was = document.createElement('s');
      was.className = 'text-xs text-compare';
      was.textContent = this.#money(product.compare_at_price);
      price.append(was);
    }
    body.append(price);

    const cta = document.createElement('span');
    cta.className =
      'mt-2 inline-flex h-10 items-center justify-center rounded-button border-2 border-interactive text-sm font-bold text-accent group-hover:bg-interactive group-hover:text-interactive-text motion-safe:transition-colors';
    cta.textContent = this.dataset.viewLabel || 'View';
    body.append(cta);

    card.append(media, body);
    item.append(card);
    return item;
  }
}

if (!customElements.get('s-recently-viewed')) customElements.define('s-recently-viewed', RecentlyViewed);
